import React from 'react';

import { Alert } from 'antd';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

import styled from 'styled-components';

import BookItem from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/BookItem';
import { NwSaveButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';

const FastPackageSortableList = styled.div`
    padding: 0.5rem 2rem 1rem;
`;

const FastPackageReorderHint = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    font-size: ${(props) => props.theme.FontSizeMedium};
    color: ${(props) => props.theme.TextColorMuted};
    line-height: 1.45;
    margin-top: 1rem;
    margin-bottom: 1rem;

    .hint-icon {
        flex-shrink: 0;
        margin-top: 0.06rem;
        font-size: ${(props) => props.theme.FontSizeLg};
        color: ${(props) => props.theme.TextColorMuted};
    }
`;

const FastPackageSortableItem = styled.div`
    width: 100%;
    box-sizing: border-box;
    background: ${(props) => (props.$isDragging ? props.theme.PrimaryColor : '#fff')};
    color: ${(props) => (props.$isDragging ? '#fff' : props.theme.TextColor)};
    border-radius: 6px;
    margin-bottom: 0.5rem;
    padding: 0.35rem 0.5rem;
    box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.12);
    display: flex;
    align-items: flex-start;
    cursor: ${(props) => (props.$isDragging ? 'grabbing' : 'grab')};

    .fast-package-sort-handle {
        flex-shrink: 0;
        margin-top: 0.5rem;
        margin-left: 0.5rem;
        padding-right: 0.25rem;
        cursor: grab;
        color: ${(props) => (props.$isDragging ? 'inherit' : props.theme.TextColorMuted)};
        line-height: 0;
    }

    .fast-package-sort-book {
        flex: 1;
        min-width: 0;
    }
`;

const FastPackageSortableBooksList = ({
    books,
    onCloseFeedback,
    onDragEnd,
    onSave,
    saveOrderFeedback,
    savingFastPackageOrder,
}) => {
    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="fast-package-books-droppable">
                {(provided) => (
                    <FastPackageSortableList ref={provided.innerRef} {...provided.droppableProps}>
                        <FastPackageReorderHint>
                            <NwIcon className="hint-icon" icon={byPrefixAndName.fasl['circle-info']} />
                            <span>
                                Drag books around to reorder them. When the order looks good, hit
                                <strong> save order</strong> so your changes stick.
                            </span>
                        </FastPackageReorderHint>
                        {books.map((book, index) => (
                            <Draggable key={book.ID} draggableId={String(book.ID)} index={index}>
                                {(dragProvided, snapshot) => (
                                    <FastPackageSortableItem
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        {...dragProvided.dragHandleProps}
                                        $isDragging={snapshot.isDragging}
                                    >
                                        <div className="fast-package-sort-handle" aria-hidden>
                                            <NwIcon icon={byPrefixAndName.fasl['sort']} size="lg" />
                                        </div>
                                        <div className="fast-package-sort-book">
                                            <BookItem
                                                book={book}
                                                showFastPackageOnlyDetails
                                                embeddedInSortableRow
                                                sortableDragging={snapshot.isDragging}
                                            />
                                        </div>
                                    </FastPackageSortableItem>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                        {saveOrderFeedback ? (
                            <Alert
                                type={saveOrderFeedback.type}
                                message={saveOrderFeedback.text}
                                showIcon
                                closable
                                onClose={onCloseFeedback}
                            />
                        ) : null}
                        <div style={{ marginTop: '2rem' }}>
                            <NwSaveButton
                                htmlType="button"
                                size="small"
                                label="save order"
                                loading={savingFastPackageOrder}
                                onClick={onSave}
                            />
                        </div>
                    </FastPackageSortableList>
                )}
            </Droppable>
        </DragDropContext>
    );
};

export default FastPackageSortableBooksList;
